import axios, { AxiosInstance } from 'axios';
import { environment } from '@src/config';
import { checkIsInternal } from './createStorage';
import { StorageService } from './storage.service';

const LOGIN_PATH = '/login';
const ACCESS_DENIED_PATH = '/AccessDenied';

const isRsaKeyMissingError = (error: any): boolean => {
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.Message ||
    error?.message ||
    '';

  return (
    typeof message === 'string' &&
    message.includes('RSA key with id') &&
    message.includes('not found')
  );
};

const isCriticalAuthEndpoint = (url = ''): boolean => {
  return (
    url.indexOf('api/auth/ohid/callback') !== -1 ||
    url.indexOf('provider-org-jobs/fetch-associated-providers') !== -1
  );
};


const hasValidAuthToken = (): boolean => {
  try {
    const currentUser =
      typeof window !== 'undefined'
        ? window.sessionStorage.getItem('currentUser')
        : null;

    if (currentUser) {
      const userObj = JSON.parse(currentUser);
      if (
        Array.isArray(userObj) &&
        userObj.length > 0 &&
        userObj[0]?.AccessToken
      ) {
        return true;
      }
    }
  } catch (_e) {
    // Ignore parse errors and continue with fallback checks.
  }

  if (typeof window === 'undefined') {
    return false;
  }

  return Boolean(window.localStorage.getItem('UHC_CURRENT_USER'));
};

const redirectTo = (path: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.location.pathname !== path) {
    window.location.href = path;
  }
};

const emitSignoutEvent = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent('signout'));
};

export const setupErrorInterceptor = (
  instance: AxiosInstance,
  abortPendingRequests: () => void
): void => {
  const storageService = new StorageService();

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }

      if (isRsaKeyMissingError(error)) {
        console.error(
          '[Error Interceptor] RSA key not found error - likely backend key rotation issue:',
          error
        );

        abortPendingRequests();

        if (typeof window !== 'undefined') {
          window.sessionStorage.clear();
          window.localStorage.clear();
        }

        redirectTo(LOGIN_PATH);
        return Promise.reject(error);
      }

      const status = error?.response?.status;

      if (status === 401) {
        const internalUser = checkIsInternal();

        if (internalUser) {
          abortPendingRequests();

          if (typeof window !== 'undefined') {
            window.sessionStorage.setItem('logout', 'true');
          }

          if (environment.internalSsoLogin) {
            emitSignoutEvent();
          } else if (typeof window !== 'undefined') {
            window.sessionStorage.clear();
          }

          redirectTo(LOGIN_PATH);
          return Promise.reject(error);
        }

        const requestUrl = error?.config?.url || '';

        if (isCriticalAuthEndpoint(requestUrl)) {
          const errorMessage =
            error?.response?.data?.Message ||
            error?.response?.data?.message ||
            error?.message;

          storageService.setAppEventDataLayerForAdobeOnAccessDenied(
            errorMessage,
            status
          );
          redirectTo(ACCESS_DENIED_PATH);
        } else {
          console.warn(
            'Error Interceptor: 401 error on non-critical endpoint, NOT clearing sessionStorage:',
            {
              url: requestUrl,
              status,
            }
          );

          if (!hasValidAuthToken()) {
            if (typeof window !== 'undefined') {
              window.sessionStorage.clear();
            }

            redirectTo(LOGIN_PATH);
          }
        }
      }

      return Promise.reject(error);
    }
  );
};
